import { useRef, useEffect, useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const Awards = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const particlesRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    
    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer = setTimeout(() => {
      if (currentRef) {
        // Definir um threshold menor para dispositivos móveis
        const isMobile = window.innerWidth < 768;
        const threshold = isMobile ? 0.05 : 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: isMobile ? '150px 0px' : '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Efeito para gerenciar as partículas
  useEffect(() => {
    const canvas = particlesRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Ajustar o canvas para cobrir toda a área da seção
    const resizeCanvas = () => {
      const sectionElement = ref.current;
      if (!sectionElement) return;
      
      const { width, height } = sectionElement.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;
    };

    // Inicializar o canvas com tamanho correto
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Configurações das partículas
    const particlesArray: Particle[] = [];
    const numberOfParticles = 80;
    
    // Classe para as partículas
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      
      constructor() {
        this.x = Math.random() * (canvas?.width || 0);
        this.y = Math.random() * (canvas?.height || 0);
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = this.getRandomColor();
      }
      
      getRandomColor(): string {
        const colors = ['rgba(255, 176, 63, 0.5)', 'rgba(59, 130, 246, 0.5)', 'rgba(255, 255, 255, 0.5)'];
        return colors[Math.floor(Math.random() * colors.length)];
      }
      
      update() {
        if (!canvas) return;
        
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }
      
      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
      }
    }
    
    // Inicializando partículas
    const init = () => {
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    
    init();
    
    // Função para conectar partículas próximas com linhas
    const connect = () => {
      if (!ctx) return;
      
      const maxDistance = 100;
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a; b < particlesArray.length; b++) {
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < maxDistance) {
            const opacity = 1 - distance / maxDistance;
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity * 0.2})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
    };
    
    // Loop de animação
    let animationId: number;
    const animate = () => {
      if (!ctx) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
      }
      
      connect();
      animationId = requestAnimationFrame(animate);
    };
    
    if (isInView) {
      animate();
    }
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, [isInView]);

  const awards = [
    {
      position: "1º lugar",
      prize: "Prêmio 1 + Bolsa 100% em curso de programação",
      icon: "🏆",
      color: "linear-gradient(to bottom right, #fcd34d, #f59e0b)",
      shadowColor: "rgba(253, 230, 138, 0.5)"
    },
    {
      position: "2º lugar",
      prize: "Prêmio 2 + Bolsa 75% em curso de programação",
      icon: "🥈",
      color: "linear-gradient(to bottom right, #d1d5db, #9ca3af)",
      shadowColor: "rgba(229, 231, 235, 0.5)"
    },
    {
      position: "3º lugar",
      prize: "Prêmio 3 + Bolsa 50% em curso de programação",
      icon: "🥉",
      color: "linear-gradient(to bottom right, #b45309, #92400e)",
      shadowColor: "rgba(217, 119, 6, 0.3)"
    }
  ];

  const organizers = [
    {
      name: "Happy Code",
      logo: "/images/realizadores/happy-logo.png",
      delay: 0.1
    },
    {
      name: "Escola ADV",
      logo: "/images/realizadores/logo-adv.png",
      delay: 0.2
    }
  ];

  const sponsors = [
    {
      name: "Logitech",
      logo: "/images/patrocinadores/logi-logo.png",
      delay: 0.3
    }
  ];

  // Decorações para o fundo
  const decorations = [
    { top: "10%", left: "5%", size: "100px", color: "rgba(30, 58, 138, 0.3)", delay: 0.2 },
    { top: "30%", right: "8%", size: "150px", color: "rgba(30, 58, 138, 0.2)", delay: 0.3 },
    { bottom: "15%", left: "10%", size: "120px", color: "rgba(30, 58, 138, 0.25)", delay: 0.4 }
  ];

  const sectionStyle: CSSProperties = {
    position: 'relative',
    padding: '5rem 0',
    overflow: 'hidden',
    background: 'linear-gradient(to bottom, #1e3a8a, #172554)',
    color: 'white',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1.5rem',
    position: 'relative',
    zIndex: 10,
  };

  const textCenterStyle: CSSProperties = {
    textAlign: 'center',
    marginBottom: '4rem',
  };

  const headingStyle: CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    fontFamily: 'Kobe, sans-serif'
  };

  const dividerStyle: CSSProperties = {
    height: '0.25rem',
    width: '5rem',
    backgroundColor: '#ffb03f',
    margin: '0 auto 2rem auto',
    borderRadius: '9999px',
  };

  const descriptionStyle: CSSProperties = {
    fontSize: '1.25rem',
    color: '#93c5fd',
    maxWidth: '48rem',
    margin: '0 auto',
  };

  const gridContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '2rem',
    maxWidth: '75rem',
    margin: '0 auto',
  };

  const partnersContainerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '6rem',
    gap: '3rem',
  };

  const partnersRowStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '3rem',
  };

  const partnerBoxStyle: CSSProperties = {
    flex: '1',
    minWidth: '300px',
    maxWidth: '550px',
    backgroundColor: 'rgba(30, 58, 138, 0.6)',
    borderRadius: '1rem',
    padding: '2rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  };

  const partnerTitleStyle: CSSProperties = {
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center',
    position: 'relative',
  };

  const partnerTitleLineStyle: CSSProperties = {
    content: '""',
    position: 'absolute',
    bottom: '-0.75rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '50px',
    height: '3px',
    backgroundColor: '#ffb03f',
    borderRadius: '9999px',
  };

  const logosContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    flexWrap: 'wrap',
  };

  const logoBoxStyle: CSSProperties = {
    width: '150px',
    height: '100px',
    backgroundColor: 'transparent',
    borderRadius: '0.75rem',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const titleUnderlineStyle: CSSProperties = {
    height: '0.25rem',
    width: '5rem',
    backgroundColor: '#ffb03f',
    margin: '0.25rem auto 3rem auto',
  };

  const prizeUnderlineStyle: CSSProperties = {
    height: '0.25rem',
    width: '3rem',
    backgroundColor: '#ffb03f',
    margin: '0.25rem auto 1.5rem auto',
  };

  const canvasStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1,
  };

  return (
    <section id="awards" style={sectionStyle} ref={ref}>
      {/* Canvas para as partículas */}
      <canvas ref={particlesRef} style={canvasStyle}></canvas>
      
      {/* Elementos decorativos */}
      {decorations.map((decoration, index) => (
        <motion.div
          key={index}
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 0.3 }}
          viewport={{ once: true }}
          transition={{ 
            duration: 0.8, 
            delay: decoration.delay,
            type: "spring",
            stiffness: 40
          }}
          style={{
            position: 'absolute',
            width: decoration.size,
            height: decoration.size,
            borderRadius: '50%',
            backgroundColor: decoration.color,
            top: decoration.top || undefined,
            left: decoration.left || undefined,
            right: decoration.right || undefined,
            bottom: decoration.bottom || undefined,
            zIndex: 0,
          }}
        />
      ))}
      
      <div style={containerStyle}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={textCenterStyle}
        >
          <h2 style={headingStyle} className="title-font">Premiação por participante</h2>
          <div style={dividerStyle}></div>
          <p style={{ fontSize: '0.875rem', color: '#cbd5e1', margin: '0 auto' }}>
            *imagens meramente ilustrativas
          </p>
        </motion.div>

        <div style={gridContainerStyle}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
            style={{
              borderRadius: '1rem',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ 
              width: '100%', 
              height: '350px', 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            }}>
              <img
                src="/images/premiacao/1º LUGAR.png"
                alt="1º Lugar"
                style={{
                  width: '85%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 100 }}
            style={{
              borderRadius: '1rem',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ 
              width: '100%', 
              height: '350px', 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            }}>
              <img
                src="/images/premiacao/2º LUGAR.png"
                alt="2º Lugar"
                style={{
                  width: '85%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4, type: "spring", stiffness: 100 }}
            style={{
              borderRadius: '1rem',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ 
              width: '100%', 
              height: '350px', 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            }}>
              <img
                src="/images/premiacao/3º LUGAR.png"
                alt="3º Lugar"
                style={{
                  width: '85%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </motion.div>
        </div>

        <div style={partnersContainerStyle}>
          <div style={partnersRowStyle}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              style={partnerBoxStyle}
            >
              <div style={partnerTitleStyle}>
                Realização
                <div style={partnerTitleLineStyle}></div>
              </div>
              <div style={logosContainerStyle}>
                {organizers.map((org, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: org.delay + 0.6 }}
                    style={logoBoxStyle}
                  >
                    <img
                      src={org.logo}
                      alt={org.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              style={partnerBoxStyle}
            >
              <div style={partnerTitleStyle}>
                Patrocínio
                <div style={partnerTitleLineStyle}></div>
              </div>
              <div style={logosContainerStyle}>
                {sponsors.map((sponsor, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: sponsor.delay + 0.8 }}
                    style={logoBoxStyle}
                  >
                    <img
                      src={sponsor.logo}
                      alt={sponsor.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards; 